import { useCallback, useEffect, useRef, useState, type Key } from 'react'
import {
  Button, Card, Col, Form, Input, InputNumber, message, Modal, Popconfirm, QRCode, Radio,
  Row, Select, Space, Switch, Table, Tabs, Tag, Tooltip, Typography, Empty,
} from 'antd'
import {
  ApiOutlined, FilterOutlined, LaptopOutlined,
  SafetyCertificateOutlined, ThunderboltOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import '../../../styles/aurora-page.css'
import '../../../styles/aurora-modal.css'
import {
  clearCaptureFlows, deleteCaptureFlow, downloadCaCert, getCaptureFlow, getCaptureFlows,
  importCaptureFlows, reportCaptureSelfHost, setCaptureFilter, setCaptureNetworkCondition,
  startRecording, stopRecording,
} from '@/api/capture'
import { getServicesStatus } from '@/api/services'
import { useProjectStore } from '@/stores/project'
import { useAuthStore } from '@/stores/auth'
import type { CaptureFlow, CaptureHeaderItem, CaptureImportItem, CaptureStatus } from '@/types'
import FlowDetailDrawer from './FlowDetailDrawer'
import RewriteRulesTab from './RewriteRulesTab'

const { Text } = Typography

const monoFont = "'JetBrains Mono', 'Fira Code', 'Consolas', monospace"

const METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS']

/**
 * 代理指向地址提示：浏览器抓包场景下应使用「当前访问平台的地址」（window.location.hostname），
 * 而非后端容器内探测的 lan_ip（服务器部署时是 docker 内网 IP 如 172.18.x.x，外部不可达）
 */
const selfHostname = window.location.hostname
const selfOrigin = window.location.origin

/** 常见 HTTP 状态码（状态码筛选框内置选项） */
const STATUS_CODE_OPTIONS = [
  200, 201, 204, 206, 301, 302, 304, 307, 308,
  400, 401, 403, 404, 405, 408, 409, 410, 413, 422, 429,
  500, 501, 502, 503, 504,
]

const methodColor: Record<string, string> = {
  GET: 'blue', POST: 'success', PUT: 'orange', DELETE: 'red', PATCH: 'purple', HEAD: 'cyan', OPTIONS: 'default',
}

/** 状态码 → Tag 颜色（0=无响应灰） */
const statusCodeTag = (code: number) => {
  if (code <= 0) return <Tag>无响应</Tag>
  const color = code < 300 ? 'success' : code < 400 ? 'processing' : code < 500 ? 'warning' : 'error'
  return <Tag color={color}>{code}</Tag>
}

/** 默认接口名：path 最后一段非空，缺省回退 host（与后端 convert_flow_to_api 口径一致） */
const defaultApiName = (flow: CaptureFlow) => {
  const seg = (flow.path || '').split('/').filter(Boolean)
  return seg.length ? seg[seg.length - 1] : flow.host || flow.path
}

/** 按请求 Content-Type 推断 body_type（与后端 _infer_body_type 口径一致） */
const inferBodyType = (headers: CaptureHeaderItem[] | undefined, body?: string) => {
  if (!body && (!headers || !headers.length)) return 'none'
  const contentType = (headers || []).find((h) => h.key.toLowerCase() === 'content-type')?.value.toLowerCase() || ''
  if (contentType.includes('application/json')) return 'json'
  if (contentType.includes('multipart/form-data')) return 'form-data'
  if (contentType.includes('application/x-www-form-urlencoded')) return 'x-www-form-urlencoded'
  if (contentType.includes('xml')) return 'xml'
  return body ? 'raw' : 'none'
}

export default function Capture() {
  const navigate = useNavigate()
  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0

  // 代理状态
  const [status, setStatus] = useState<CaptureStatus | null>(null)
  // 列表
  const [flows, setFlows] = useState<CaptureFlow[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 })
  // 导入统计（同列表筛选条件：未导入/已导入记录数）
  const [importStats, setImportStats] = useState({ unimported: 0, imported: 0 })
  // 筛选
  const [methodFilter, setMethodFilter] = useState('')
  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState<number>(0)
  const [importedFilter, setImportedFilter] = useState(-1)
  // 自动刷新（3s 轮询开关）
  const [autoRefresh, setAutoRefresh] = useState(true)
  // 派生状态
  const running = status?.running ?? false
  const recording = status?.recording ?? false
  // 勾选
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
  // 详情抽屉
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [detailId, setDetailId] = useState<number | null>(null)
  // 导入弹窗
  const [importOpen, setImportOpen] = useState(false)
  // CA 证书下载与安装引导弹窗
  const [certOpen, setCertOpen] = useState(false)
  const [importItems, setImportItems] = useState<CaptureImportItem[]>([])
  const [importing, setImporting] = useState(false)
  // 域名过滤弹窗（运行期内存态）
  const [filterOpen, setFilterOpen] = useState(false)
  const [filterMode, setFilterMode] = useState<'off' | 'allow' | 'deny'>('off')
  const [filterHosts, setFilterHosts] = useState<string[]>([])
  const [filterSaving, setFilterSaving] = useState(false)
  // 弱网弹窗（运行期内存态）
  const [networkOpen, setNetworkOpen] = useState(false)
  const [netReqDelay, setNetReqDelay] = useState(0)
  const [netRespDelay, setNetRespDelay] = useState(0)
  const [netOffline, setNetOffline] = useState(false)
  const [networkSaving, setNetworkSaving] = useState(false)
  // 重写规则页签数量（由 RewriteRulesTab 上报）
  const [ruleTotal, setRuleTotal] = useState(0)

  /** 加载代理状态 */
  const loadStatus = useCallback(async () => {
    try {
      // 抓包代理状态由 /services/status 统一返回（capture 字段）
      setStatus((await getServicesStatus()).capture)
    } catch {
      /* 拦截器已提示 */
    }
  }, [])

  /** 加载抓包列表（静默刷新时不重置页码/不显示 loading） */
  const loadFlows = useCallback(
    async (page = pagination.page, pageSize = pagination.pageSize, silent = false) => {
      if (!silent) setLoading(true)
      try {
        const res = await getCaptureFlows({
          page,
          page_size: pageSize,
          method: methodFilter || undefined,
          keyword: keyword || undefined,
          status_code: statusFilter || undefined,
          imported: importedFilter >= 0 ? importedFilter : undefined,
        })
        setFlows(res.data)
        setPagination((prev) => ({ ...prev, page, pageSize, total: res.total }))
        setImportStats({ unimported: res.unimported_count ?? 0, imported: res.imported_count ?? 0 })
        // 勾选中已不在当前列表的项，保持勾选（跨页勾选）
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [pagination.page, pagination.pageSize, methodFilter, keyword, statusFilter, importedFilter],
  )

  // 首次加载
  useEffect(() => {
    loadStatus()
    loadFlows(1)
    // 上报平台自身地址：浏览器代理抓包时平台自身页面/API 流量需被代理恒放行（不拦不录）
    reportCaptureSelfHost([selfHostname]).catch(() => { /* 上报失败不影响页面 */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 筛选变化 → 重新加载第一页（跳过首次挂载：首次加载由上方 effect 负责，避免同一请求发两遍）
  const filterMountedRef = useRef(false)
  useEffect(() => {
    if (!filterMountedRef.current) {
      filterMountedRef.current = true
      return
    }
    loadFlows(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methodFilter, keyword, statusFilter, importedFilter])

  // 实时推送（SSE）：录制 + 自动刷新开启时订阅，入库即推送新记录（免轮询）；断流自动降级 3s 轮询
  useEffect(() => {
    if (!autoRefresh || !recording) return
    let stopped = false
    let controller: AbortController | null = null
    let pollTimer: number | null = null

    const startPolling = () => {
      if (pollTimer !== null || stopped) return
      pollTimer = window.setInterval(() => {
        loadStatus()
        loadFlows(pagination.page, pagination.pageSize, true)
      }, 3000)
    }

    // 收到推送：仅第一页且无筛选时原地插入新记录；翻页/筛选中不动列表（下拉/翻页全量加载时自然生效）
    const applyBriefs = (briefs: CaptureFlow[]) => {
      loadStatus()
      const hasFilter = !!(methodFilter || keyword || statusFilter || importedFilter >= 0)
      if (pagination.page !== 1 || hasFilter) return
      setFlows((prev) => {
        const seen = new Set(prev.map((f) => f.id))
        const fresh = briefs.filter((f) => !seen.has(f.id))
        if (!fresh.length) return prev
        return [...fresh, ...prev].slice(0, pagination.pageSize)
      })
    }

    // EventSource 不支持自定义 Authorization header，故用 fetch 流式读取 SSE（与 UIAutomation 同模式）
    const startStream = async () => {
      controller = new AbortController()
      try {
        const token = useAuthStore.getState().token
        const res = await fetch('/api/v1/capture/flows/stream', {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          signal: controller.signal,
        })
        if (!res.ok || !res.body) throw new Error(`SSE 连接失败: HTTP ${res.status}`)
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buf = ''
        for (;;) {
          const { done, value } = await reader.read()
          if (stopped) return
          if (done) break
          buf += decoder.decode(value, { stream: true })
          let idx: number
          while ((idx = buf.indexOf('\n\n')) >= 0) {
            const chunk = buf.slice(0, idx)
            buf = buf.slice(idx + 2)
            const line = chunk.split('\n').find((l) => l.startsWith('data: '))
            if (line) {
              try {
                applyBriefs(JSON.parse(line.slice(6)))
              } catch {
                /* 忽略坏帧 */
              }
            }
          }
        }
      } catch (err) {
        if (stopped || (err instanceof DOMException && err.name === 'AbortError')) return
      }
      if (!stopped) startPolling()
    }

    startStream()
    return () => {
      stopped = true
      controller?.abort()
      if (pollTimer !== null) window.clearInterval(pollTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, recording, pagination.page, pagination.pageSize, methodFilter, keyword, statusFilter, importedFilter])

  /** 开始录制：仅打开录制开关（代理未运行时后端自动拉起），代理保持常驻；录制时默认开启自动刷新 */
  const handleStartRecord = async () => {
    try {
      await startRecording()
      message.success('开始录制，请将客户端 HTTP 代理指向 ' + (selfHostname || status?.lan_ip || '本机') + ':' + (status?.port ?? 8080))
      setAutoRefresh(true)
      loadStatus()
      loadFlows(1)
    } catch {
      /* 拦截器已提示 */
    }
  }

  /** 停止录制：暂停入库与轮询，代理保持运行 */
  const handleStopRecord = async () => {
    try {
      await stopRecording()
      message.success('已停止录制（代理保持运行）')
      setAutoRefresh(false)
      loadStatus()
    } catch {
      /* 拦截器已提示 */
    }
  }

  /** 清空 */
  const handleClear = async () => {
    await clearCaptureFlows()
    message.success('已清空全部抓包记录')
    setSelectedRowKeys([])
    loadFlows(1)
  }

  /** 删除单条 */
  const handleDelete = async (id: number) => {
    await deleteCaptureFlow(id)
    message.success('删除成功')
    setSelectedRowKeys((keys) => keys.filter((k) => k !== id))
    loadFlows()
  }

  /** 导入预览项组装（path 完整展示，query 合并进 Path；导入时后端按 ? 拆分） */
  const buildImportItem = (f: CaptureFlow): CaptureImportItem => ({
    flow_id: f.id,
    name: defaultApiName(f),
    method: f.method,
    host: `${f.scheme}://${f.host}${f.port && f.port !== 80 && f.port !== 443 ? `:${f.port}` : ''}`,
    path: f.path,
    query: f.query || '',
    body_type: inferBodyType(f.req_headers, f.req_body),
    headers: f.req_headers || [],
    body: f.req_body || '',
  })

  /** 打开批量导入弹窗（勾选记录） */
  const openBatchImport = async () => {
    if (!projectId) {
      message.warning('请先在顶部选择目标项目')
      return
    }
    const selected = flows.filter((f) => selectedRowKeys.includes(f.id))
    setImportItems(selected.map(buildImportItem))
    setImportOpen(true)
    // 列表接口不含请求头/请求体，并发拉详情补全展示（保留用户已编辑的名称）
    const details = await Promise.all(selected.map((f) => getCaptureFlow(f.id).catch(() => null)))
    setImportItems((items) =>
      items.map((it, i) => {
        const d = details[i]
        return d ? { ...buildImportItem(d), name: it.name } : it
      }),
    )
  }

  /** 打开单条导入弹窗（详情抽屉，flow 为全量详情） */
  const openSingleImport = (flow: CaptureFlow) => {
    if (!projectId) {
      message.warning('请先在顶部选择目标项目')
      return
    }
    setDetailId(flow.id)
    setImportItems([buildImportItem(flow)])
    setImportOpen(true)
  }

  /** 提交导入 */
  const submitImport = async () => {
    if (!importItems.length) return
    setImporting(true)
    try {
      const res = await importCaptureFlows({
        flow_ids: importItems.map((i) => i.flow_id),
        project_id: projectId,
      })
      message.success(`已导入 ${res.imported} 条到接口列表`)
      setImportOpen(false)
      setSelectedRowKeys([])
      loadFlows()
    } finally {
      setImporting(false)
    }
  }

  /** 打开域名过滤弹窗（回填当前运行期配置） */
  const openFilterModal = () => {
    setFilterMode(status?.filter?.mode ?? 'off')
    setFilterHosts(status?.filter?.hosts ?? [])
    setFilterOpen(true)
  }

  /** 保存域名过滤 */
  const submitFilter = async () => {
    setFilterSaving(true)
    try {
      await setCaptureFilter({ mode: filterMode, hosts: filterMode === 'off' ? [] : filterHosts })
      message.success(filterMode === 'off' ? '已关闭域名过滤' : '域名过滤已生效')
      setFilterOpen(false)
      loadStatus()
    } catch {
      /* 拦截器已提示 */
    } finally {
      setFilterSaving(false)
    }
  }

  /** 打开弱网弹窗（回填当前运行期配置） */
  const openNetworkModal = () => {
    setNetReqDelay(status?.network?.request_delay_ms ?? 0)
    setNetRespDelay(status?.network?.response_delay_ms ?? 0)
    setNetOffline(status?.network?.offline ?? false)
    setNetworkOpen(true)
  }

  /** 保存弱网配置 */
  const submitNetwork = async () => {
    setNetworkSaving(true)
    try {
      await setCaptureNetworkCondition({
        request_delay_ms: netReqDelay,
        response_delay_ms: netRespDelay,
        offline: netOffline,
      })
      message.success(netOffline ? '已开启断网模拟' : '弱网配置已生效')
      setNetworkOpen(false)
      loadStatus()
    } catch {
      /* 拦截器已提示 */
    } finally {
      setNetworkSaving(false)
    }
  }

  // ============ 表格列 ============
  const columns: ColumnsType<CaptureFlow> = [
    {
      title: '方法',
      dataIndex: 'method',
      width: 108,
      render: (m: string, r: CaptureFlow) => (
        <Space size={4} wrap={false}>
          <Tag color={methodColor[m] || 'default'}>{m}</Tag>
          {r.is_replay && <Tag color="purple">重发</Tag>}
        </Space>
      ),
    },
    { title: 'Host', dataIndex: 'host', width: 128, ellipsis: true },
    {
      title: 'Path',
      dataIndex: 'path',
      width: 300,
      ellipsis: true,
      render: (v: string, r: CaptureFlow) => (
        <Tooltip title={r.error_msg || `${v}${r.query ? `?${r.query}` : ''}`}>
          <span style={{ fontFamily: monoFont, fontSize: 13 }}>{v}</span>
          {r.error_msg && <Text type="danger" style={{ marginLeft: 6, fontSize: 12 }}>（{r.error_msg}）</Text>}
        </Tooltip>
      ),
    },
    { title: '状态码', dataIndex: 'status_code', width: 80, align: 'center', render: (c: number) => statusCodeTag(c) },
    {
      title: 'Content-Type',
      dataIndex: 'content_type',
      width: 104,
      ellipsis: true,
      render: (v: string) => <Text type="secondary" style={{ fontSize: 12 }}>{v || '-'}</Text>,
    },
    { title: '耗时', dataIndex: 'duration_ms', width: 78, align: 'right', render: (v: number) => <span style={{ fontFamily: monoFont, fontSize: 13 }}>{v} ms</span> },
    { title: '时间', dataIndex: 'start_time', width: 150, render: (v: string) => <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> },
    {
      title: '已导入',
      dataIndex: 'is_imported',
      width: 86,
      align: 'center',
      render: (v: boolean, r: CaptureFlow) =>
        v ? (
          <Tooltip title="点击跳转接口列表">
            <Tag color="success" style={{ cursor: 'pointer' }} onClick={() => navigate(`/api-test?highlight=${r.imported_api_id}`)}>
              已导入
            </Tag>
          </Tooltip>
        ) : (
          <Tag>未导入</Tag>
        ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 116,
      render: (_, record: CaptureFlow) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              setDetailId(record.id)
              setDrawerOpen(true)
            }}
          >
            详情
          </Button>
          <Popconfirm
            title="确定删除该记录？"
            okText="删除"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record.id)}
            onCancel={(e) => e?.stopPropagation()}
          >
            <Button
              type="link"
              size="small"
              danger
              className="danger"
              onClick={(e) => e.stopPropagation()}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="aurora-page aurora-page--fit">
      {/* 页头 */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Text style={{ fontSize: 24 }}>抓包</Text>
        <Text type="secondary" className="page-header-subtitle" style={{ fontSize: 11 }}>
          仅用于对自有系统/获授权目标的测试调试；App 存在证书锁定（SSL Pinning）时无法抓取，属预期限制
        </Text>
      </div>

      {/* 状态卡片行（竖向紧凑布局：标题行 / 数值 / 说明，可操作卡整卡点击） */}
      <Row gutter={[16, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} md={8} xl={4}>
          <Card className="stat-card" style={{ height: '100%' }} styles={{ body: { padding: '10px 12px' } }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div className="stat-card-icon" style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: running ? 'var(--au-sage-100)' : 'var(--au-terracotta-light)', color: running ? 'var(--au-sage-700)' : 'var(--au-terracotta)' }}>
                <ThunderboltOutlined style={{ fontSize: 13 }} />
              </div>
              <span className="stat-card-title" style={{ flex: 1 }}>代理状态</span>
            </div>
            <div className="stat-card-value" style={{ fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: running ? 'var(--au-sage-700)' : 'var(--au-terracotta)' }}>
              {running ? '运行中' : '已停止'}
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {recording ? '● 录制中' : running ? '代理转发中（未录制）' : '代理未运行'}
            </Text>
          </Card>
        </Col>
        <Col xs={24} md={8} xl={4}>
          <Card className="stat-card" style={{ height: '100%' }} styles={{ body: { padding: '10px 12px' } }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div className="stat-card-icon" style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--au-sage-100)', color: 'var(--au-sage-700)' }}>
                <LaptopOutlined style={{ fontSize: 13 }} />
              </div>
              <span className="stat-card-title" style={{ flex: 1 }}>电脑地址</span>
            </div>
            <div className="stat-card-value" style={{ fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {selfHostname || status?.lan_ip || '--'}
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', wordBreak: 'break-all', lineHeight: 1.5 }}>
              代理端口 {status?.port ?? 8080} · 浏览器/手机代理均指向此地址
            </Text>
          </Card>
        </Col>
        <Col xs={24} md={8} xl={4}>
          <Card className="stat-card" style={{ height: '100%' }} styles={{ body: { padding: '10px 12px' } }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div className="stat-card-icon" style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--au-sand-light)', color: '#c98d2b' }}>
                <ApiOutlined style={{ fontSize: 13 }} />
              </div>
              <span className="stat-card-title" style={{ flex: 1 }}>记录概况</span>
            </div>
            <div className="stat-card-value" style={{ fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              共 {pagination.total} 条
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              未导入 {importStats.unimported} 条 · 已导入 {importStats.imported} 条
            </Text>
          </Card>
        </Col>
        <Col xs={24} md={8} xl={4}>
          <Card
            className="stat-card"
            style={{ height: '100%' }}
            styles={{ body: { padding: '10px 12px' } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div className="stat-card-icon" style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--au-terracotta-light)', color: 'var(--au-terracotta)' }}>
                <SafetyCertificateOutlined style={{ fontSize: 13 }} />
              </div>
              <span className="stat-card-title" style={{ flex: 1 }}>证书下载</span>
              <Button
                type="primary"
                size="small"
                style={{ borderRadius: 999, fontWeight: 700, height: 30, paddingInline: 14 }}
                onClick={() => setCertOpen(true)}
              >
                下载
              </Button>
            </div>
            <div className="stat-card-value" style={{ fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              CA 证书
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {status?.ca_exists ? 'HTTPS 抓包需安装并信任' : '首次录制后自动生成'}
            </Text>
          </Card>
        </Col>
        <Col xs={24} md={8} xl={4}>
          <Card
            className="stat-card"
            style={{ height: '100%' }}
            styles={{ body: { padding: '10px 12px' } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div className="stat-card-icon" style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--au-sage-100)', color: 'var(--au-sage-700)' }}>
                <FilterOutlined style={{ fontSize: 13 }} />
              </div>
              <span className="stat-card-title" style={{ flex: 1 }}>域名过滤</span>
              <Button
                size="small"
                style={{ borderRadius: 999, height: 30, paddingInline: 14 }}
                onClick={openFilterModal}
              >
                配置
              </Button>
            </div>
            <div className="stat-card-value" style={{ fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {(() => {
                const mode = status?.filter?.mode ?? 'off'
                return mode === 'allow' ? '白名单' : mode === 'deny' ? '黑名单' : '不过滤'
              })()}
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {(status?.filter?.hosts ?? []).length ? `规则 ${(status?.filter?.hosts ?? []).length} 条，点击调整` : '未启用，点击配置'}
            </Text>
          </Card>
        </Col>
        <Col xs={24} md={8} xl={4}>
          <Card
            className="stat-card"
            style={{ height: '100%' }}
            styles={{ body: { padding: '10px 12px' } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div className="stat-card-icon" style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: netOffline ? 'var(--au-terracotta-light)' : 'var(--au-sand-light)', color: netOffline ? 'var(--au-terracotta)' : '#c98d2b' }}>
                <ThunderboltOutlined style={{ fontSize: 13 }} />
              </div>
              <span className="stat-card-title" style={{ flex: 1 }}>弱网模拟</span>
              <Button
                size="small"
                style={{ borderRadius: 999, height: 30, paddingInline: 14 }}
                onClick={openNetworkModal}
              >
                配置
              </Button>
            </div>
            <div className="stat-card-value" style={{ fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: netOffline ? 'var(--au-terracotta)' : undefined }}>
              {netOffline ? '断网中' : (status?.network?.request_delay_ms ?? 0) + (status?.network?.response_delay_ms ?? 0) > 0 ? '延迟模拟' : '正常'}
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              请求 {status?.network?.request_delay_ms ?? 0} ms · 响应 {status?.network?.response_delay_ms ?? 0} ms
            </Text>
          </Card>
        </Col>
      </Row>

      {/* 主内容卡片：Tabs 内嵌（与「自愈与缓存」页结构统一） */}
      <div className="content-card">
        <Tabs
          defaultActiveKey="flows"
          tabBarStyle={{ paddingLeft: 22 }}
          items={[
            {
              key: 'flows',
              label: `抓包记录（${pagination.total}）`,
              children: (
                <>
                  <div className="search-bar" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap', padding: '0 22px' }}>
              <Input
                allowClear
                placeholder="搜索 Host / Path"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                style={{ width: 220, borderRadius: 999 }}
              />
              <Select
                value={methodFilter || undefined}
                onChange={(v) => setMethodFilter(v || '')}
                allowClear
                placeholder="方法"
                style={{ width: 110 }}
                options={METHOD_OPTIONS.map((m) => ({ value: m, label: m }))}
              />
              <Select
                value={statusFilter || undefined}
                onChange={(v) => setStatusFilter(v || 0)}
                allowClear
                showSearch
                placeholder="状态码"
                style={{ width: 110 }}
                options={STATUS_CODE_OPTIONS.map((code) => ({ value: code, label: String(code) }))}
              />
              <Select
                value={importedFilter}
                onChange={setImportedFilter}
                style={{ width: 110 }}
                options={[
                  { value: -1, label: '全部' },
                  { value: 0, label: '未导入' },
                  { value: 1, label: '已导入' },
                ]}
              />
              <div style={{ flex: 1 }} />
              <Tooltip title={autoRefresh ? '暂停自动刷新（3s 轮询）' : '恢复自动刷新'}>
                <Switch
                  checkedChildren="刷新"
                  unCheckedChildren="暂停"
                  checked={autoRefresh}
                  onChange={setAutoRefresh}
                />
              </Tooltip>
              <Button onClick={() => { loadStatus(); loadFlows() }}>刷新</Button>
              <Popconfirm
                title="确定清空全部抓包记录？（软删除，不可恢复）"
                okText="确定"
                okButtonProps={{ danger: true }}
                onConfirm={handleClear}
              >
                <Button danger>清空</Button>
              </Popconfirm>
              {recording ? (
                <Button danger onClick={handleStopRecord}>停止录制</Button>
              ) : (
                <Button type="primary" onClick={handleStartRecord}>开始录制</Button>
              )}
              <Button type="primary" disabled={selectedRowKeys.length === 0} onClick={openBatchImport} style={{ borderRadius: 999, fontWeight: 700 }}>
                导入 {selectedRowKeys.length} 条到接口列表
              </Button>
                  </div>
                  <Table<CaptureFlow>
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    dataSource={flows}
                    scroll={{ y: '100%' }}
                    rowSelection={{
                      selectedRowKeys,
                      onChange: (keys) => setSelectedRowKeys(keys),
                      // 全局表格单元格左右各 22px 内边距，加宽选择列避免复选框被裁切
                      columnWidth: 60,
                      selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
                    }}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={running ? '暂无抓包记录，请将客户端代理指向本机端口后发起请求' : '代理未启动，点击上方「开始录制」按钮开始抓包'} /> }}
                    onRow={(record) => ({
                      style: { cursor: 'pointer' },
                      onClick: (e) => {
                        const target = e.target as HTMLElement
                        if (target.closest('input,button,a,.ant-checkbox-wrapper,.ant-popover')) return
                        setDetailId(record.id)
                        setDrawerOpen(true)
                      },
                    })}
                    pagination={{
                      current: pagination.page,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      showSizeChanger: true,
                      showTotal: (t) => `共 ${t} 条`,
                      onChange: (p, ps) => loadFlows(p, ps),
                    }}
                  />
                </>
              ),
            },
            { key: 'rewrite', label: `重写规则（${ruleTotal}）`, children: <RewriteRulesTab onCountChange={setRuleTotal} /> },
          ]}
        />
      </div>

      {/* 详情抽屉 */}
      <FlowDetailDrawer
        open={drawerOpen}
        flowId={detailId}
        onClose={() => setDrawerOpen(false)}
        onImport={openSingleImport}
        onRepeated={() => loadFlows()}
      />

      {/* 导入弹窗 */}
      <Modal
        title={`导入 ${importItems.length} 条到接口列表`}
        open={importOpen}
        onCancel={() => setImportOpen(false)}
        onOk={submitImport}
        confirmLoading={importing}
        okText="确认导入"
        cancelText="取消"
        width={1200}
        destroyOnClose
      >
        <Form layout="inline" style={{ marginBottom: 12 }}>
          <Form.Item label="目标项目">
            <Text strong>{currentProject?.name || '未选择'}</Text>
          </Form.Item>
        </Form>
        <Table<CaptureImportItem>
          rowKey="flow_id"
          size="small"
          pagination={false}
          dataSource={importItems}
          columns={[
            {
              title: '接口名称',
              dataIndex: 'name',
              width: 180,
              render: (v: string, r: CaptureImportItem) => (
                <Input
                  size="small"
                  value={v}
                  onChange={(e) =>
                    setImportItems((items) => items.map((it) => (it.flow_id === r.flow_id ? { ...it, name: e.target.value } : it)))
                  }
                />
              ),
            },
            { title: '方法', dataIndex: 'method', width: 80, render: (m: string) => <Tag color={methodColor[m] || 'default'}>{m}</Tag> },
            { title: 'Host', dataIndex: 'host', width: 200, ellipsis: true, render: (v: string) => <span style={{ fontFamily: monoFont, fontSize: 12 }}>{v}</span> },
            {
              title: 'Path',
              dataIndex: 'path',
              width: 280,
              ellipsis: true,
              render: (v: string, r: CaptureImportItem) => (
                <Tooltip title={`${v}${r.query ? `?${r.query}` : ''}`}>
                  <span style={{ fontFamily: monoFont, fontSize: 12 }}>{v}{r.query ? `?${r.query}` : ''}</span>
                </Tooltip>
              ),
            },
            {
              title: '请求头',
              dataIndex: 'headers',
              width: 90,
              render: (h: CaptureHeaderItem[] | undefined) =>
                h && h.length ? (
                  <Tooltip
                    placement="bottomLeft"
                    overlayStyle={{ maxWidth: 420 }}
                    title={
                      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                        {h.map((it, i) => (
                          <div key={i} style={{ fontFamily: monoFont, fontSize: 12 }}>
                            {it.key}: {it.value}
                          </div>
                        ))}
                      </div>
                    }
                  >
                    <span style={{ cursor: 'help' }}>{h.length} 个</span>
                  </Tooltip>
                ) : (
                  '-'
                ),
            },
            { title: '请求体类型', dataIndex: 'body_type', width: 130 },
            {
              title: '请求体',
              dataIndex: 'body',
              ellipsis: true,
              render: (v: string | undefined) =>
                v ? (
                  <Tooltip
                    placement="bottomLeft"
                    overlayStyle={{ maxWidth: 460 }}
                    title={<div style={{ maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{v}</div>}
                  >
                    <span style={{ fontFamily: monoFont, fontSize: 12 }}>{v}</span>
                  </Tooltip>
                ) : (
                  '-'
                ),
            },
          ]}
          footer={() => <Text type="secondary" style={{ fontSize: 12 }}>同项目内按 方法+Host+Path 判重，已存在则更新而非重复创建；Path 中 ? 之后的 Query 参数会在导入时自动拆分为 Query 参数</Text>}
        />
      </Modal>

      {/* CA 证书下载与安装引导弹窗（HTTPS 抓包必须信任 CA） */}
      <Modal
        title="CA 证书下载与信任引导"
        open={certOpen}
        onCancel={() => setCertOpen(false)}
        footer={[<Button key="ok" type="primary" style={{ borderRadius: 999 }} onClick={() => setCertOpen(false)}>我知道了</Button>]}
        width={680}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
          <div style={{ background: 'var(--au-terracotta-light, #fdf0ec)', borderRadius: 8, padding: '10px 14px' }}>
            <Text strong style={{ color: 'var(--au-terracotta)' }}>
              说明：HTTPS 抓包依赖中间人（MITM）解密，客户端必须安装并信任本平台 CA 证书，否则 HTTPS 请求只显示域名无法查看内容。
            </Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Text strong>第一步：下载证书（首次开始录制后自动生成）</Text>
            <Button
              size="small"
              type="primary"
              style={{ borderRadius: 999, fontWeight: 700 }}
              disabled={!status?.ca_exists}
              onClick={downloadCaCert}
            >
              下载 auroratest-ca-cert.crt
            </Button>
            {!status?.ca_exists && <Tag color="warning">证书尚未生成，请先点击「开始录制」</Tag>}
          </div>
          <div>
            <Text strong>第二步：在客户端安装并信任（按设备选择）</Text>
            <ul style={{ paddingLeft: 20, margin: '8px 0 0', lineHeight: 1.9 }}>
              <li><Text strong>Windows：</Text>双击下载的 .crt 文件 → 「安装证书」→ 当前用户 →「将所有的证书都放入下列存储」→ 浏览选择「受信任的根证书颁发机构」→ 完成；浏览器需重启生效。</li>
              <li><Text strong>macOS：</Text>双击证书导入「钥匙串访问」→ 在「系统」中找到 mitmproxy → 双击 → 展开「信任」→ 选「始终信任」。</li>
              <li><Text strong>iOS：</Text>Safari 打开证书下载链接或隔空投送安装描述文件 → 设置 → 通用 → VPN 与设备管理 → 安装；再到 关于本机 → 证书信任设置 → 开启 mitmproxy 的完全信任。</li>
              <li><Text strong>Android：</Text>设置 → 安全 → 更多安全设置 → 从存储安装证书；Android 7 及以上 App 默认不信任用户证书（浏览器仍可用）。</li>
            </ul>
          </div>
          <div>
            <Text strong>第三步：设置代理并开始抓包</Text>
            <ul style={{ paddingLeft: 20, margin: '8px 0 0', lineHeight: 1.9 }}>
              <li>本机浏览器：系统代理指向 <Text code>{selfHostname || '127.0.0.1'}:{status?.port ?? 8080}</Text>。</li>
              <li>手机（能访问本平台的网络）：Wi-Fi 代理配置为手动，服务器 <Text code>{selfHostname || status?.lan_ip || '平台访问地址'}</Text>，端口 <Text code>{status?.port ?? 8080}</Text>；注意放行防火墙入站规则。</li>
              <li>提示：Chrome 建议以 <Text code>--disable-quic</Text> 启动以禁用 QUIC，避免流量走 UDP 绕过代理。</li>
            </ul>
          </div>
          {/* 手机扫码接入引导（P3-3）：二维码内容含代理配置与 CA 下载链接，手机扫码即得完整配置信息 */}
          {(selfHostname || status?.lan_ip) && (
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', background: 'var(--au-primary-50, #f6f8f5)', borderRadius: 8, padding: 12 }}>
              <QRCode
                value={[
                  'AuroraTest 抓包接入指引',
                  `1. Wi-Fi 代理（手动）：服务器 ${selfHostname || status?.lan_ip}，端口 ${status?.port ?? 8080}`,
                  `2. 下载并信任 CA 证书：${selfOrigin}/api/v1/capture/ca-cert`,
                  '3. 注意放行电脑防火墙入站规则（端口 ' + (status?.port ?? 8080) + '）',
                ].join('\n')}
                size={140}
              />
              <div style={{ flex: 1 }}>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>手机扫码接入</Text>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', lineHeight: 1.6 }}>
                  手机相机扫码即得代理配置与 CA 证书下载链接（须手机可访问平台地址）。
                </Text>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* 域名过滤配置弹窗（运行期内存态） */}
      <Modal
        title="域名过滤配置"
        open={filterOpen}
        onCancel={() => setFilterOpen(false)}
        onOk={submitFilter}
        confirmLoading={filterSaving}
        okText="保存并生效"
        cancelText="取消"
        width={560}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
          <div style={{ background: 'var(--au-terracotta-light, #fdf0ec)', borderRadius: 8, padding: '10px 14px' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              配置保存在数据库，一次配置永久生效：后端重启后会自动读取并继续生效，无需重新设置。白名单=只抓列表内域名的请求（其余返回 404 不抓）；黑名单=命中请求短路返回 403。直接填域名即可，自动匹配全部子域（填 baidu.com 会抓取 www.baidu.com、map.baidu.com 等）；也可以直接粘贴 URL，自动提取域名。
            </Text>
          </div>
          <Form layout="vertical">
            <Form.Item label="过滤模式">
              <Radio.Group value={filterMode} onChange={(e) => setFilterMode(e.target.value)}>
                <Radio value="off">不过滤</Radio>
                <Radio value="allow">只抓白名单</Radio>
                <Radio value="deny">屏蔽黑名单</Radio>
              </Radio.Group>
            </Form.Item>
            {filterMode !== 'off' && (
              <Form.Item label="域名列表（每行一条，填域名或 URL 均可）">
                <Input.TextArea
                  rows={6}
                  placeholder={'baidu.com\nwww.example.com\nhttps://api.test.cn/'}
                  value={filterHosts.join('\n')}
                  onChange={(e) => setFilterHosts(e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
                  style={{ fontFamily: monoFont, fontSize: 12 }}
                />
              </Form.Item>
            )}
          </Form>
        </div>
      </Modal>

      {/* 弱网模拟配置弹窗（运行期内存态） */}
      <Modal
        title="弱网模拟配置"
        open={networkOpen}
        onCancel={() => setNetworkOpen(false)}
        onOk={submitNetwork}
        confirmLoading={networkSaving}
        okText="保存并生效"
        cancelText="取消"
        width={560}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13 }}>
          <div style={{ background: 'var(--au-terracotta-light, #fdf0ec)', borderRadius: 8, padding: '10px 14px' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              一期仅支持延迟与断网。断网开启后所有请求短路返回 503；延迟分别作用于请求与响应阶段。配置保存在数据库，一次配置永久生效：后端重启后会自动读取并继续生效，无需重新设置。
            </Text>
          </div>
          <Form layout="vertical">
            <Form.Item label="请求延迟（ms）">
              <InputNumber min={0} max={60000} value={netReqDelay} onChange={(v) => setNetReqDelay(v ?? 0)} style={{ width: 220, borderRadius: 999 }} />
            </Form.Item>
            <Form.Item label="响应延迟（ms）">
              <InputNumber min={0} max={60000} value={netRespDelay} onChange={(v) => setNetRespDelay(v ?? 0)} style={{ width: 220, borderRadius: 999 }} />
            </Form.Item>
            <Form.Item label="断网模拟">
              <Space>
                <Switch checked={netOffline} onChange={setNetOffline} />
                <Text type="secondary" style={{ fontSize: 12 }}>开启后所有请求短路返回 503</Text>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  )
}
